import * as React from 'react';
import { translate } from 'react-i18next';
import { browserHistory } from 'react-router';
import * as c from 'models/constants';
import { PortalPageSection, PageTitle, ButtonsSection } from 'components/PortalStructure';
import { reduxForm, getFormSyncErrors, Field } from 'redux-form';
import { Button, ErrorSummary, LoadingOverlayWrapper } from 'components';
import { IShopProfile } from 'models/shop/shopprofile';
import { IStore } from 'redux/IStore';
import { IManageShop, IManageShopAction } from 'models/shop/manageshop';
import { getShop, putShop } from 'modules/shop/manageshop';
import { IDomainValue } from 'models/common';
import { preventRouterLeave } from 'helpers/hooks';
import { InjectedRouter } from 'react-router';
import { ManageShopWrapper } from 'containers';
import { PortalSelectMenu } from 'components/FormControls';
require('./style.css');

const { connect } = require('react-redux');

interface IProps {
	manageShop?: IManageShop;
	currentFormValues: IShopProfile;
	initialValues: IShopProfile;
	params?: IParams;
	location: any;
	countryDomainValues: IDomainValue[];
	router: InjectedRouter;
	route: any;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	t?: (key: string) => string;
	getShop: (id: string, isShipping: boolean) => IManageShopAction;
	putShop: (shop: IShopProfile) => IManageShopAction;
}

interface IParams {
	id?: string;
}

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ManageShopShippingForm extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
	}

	public componentDidMount() {
		this.props.getShop(this.props.params.id, true);
	}

	public onSubmit(form: IShopProfile) {
		this.props.putShop(form);
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'shippingMessageTemplate':
				return t('Shop_ManageShop_Shipping_Label_MessageTemplate');

			case 'shippingFromCountry':
				return t('Shop_ManageShop_Shipping_Label_Country');

			default:
				return key;
		}
	}

	public render() {
		const { handleSubmit, t, formErrors, submitFailed, submitting, pristine, location, countryDomainValues } =
			this.props;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.manageShop ? this.props.manageShop.error : false,
			message: this.props.manageShop ? this.props.manageShop.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		let countries = [];
		if (countryDomainValues) {
			countries = countryDomainValues.map((country: IDomainValue) => {
				return {
					key: country.key,
					value: t(`DV_Countries_${country.key}`),
				};
			});
			countries.sort((a: IDomainValue, b: IDomainValue) => {
				if (a.value < b.value) {
					return -1;
				}
				if (a.value > b.value) {
					return 1;
				}
				return 0;
			});
		}

		return (
			<ManageShopWrapper id={this.props.params.id}>
				<div className="shopShippingFormWrapper">
					<PageTitle
						title={t('Shop_ManageShop_Shipping_Label_Shipping')}
						titleContent={
							<Button
								type="button"
								size="small"
								onClick={() => {
									browserHistory.push('/shop/' + this.props.params.id + '/edit/shipping/shippingoptions/create');
								}}
							>
								{t('Shop_ManageShop_Shipping_Button_AddShippingOption')}
							</Button>
						}
					/>
					<LoadingOverlayWrapper
						active={this.props.manageShop.isFetching}
						spinner={true}
						text={t('BSPortal_Common_Loading')}
						background="rgba(0, 0, 0, 0.1)"
					>
						<form onSubmit={handleSubmit(this.onSubmit)}>
							<div className="profileError">
								<ErrorSummary {...errorSummaryProps} />
							</div>
							<PortalPageSection
								title={t('Shop_ManageShop_Shipping_Label_ShippingOrigins')}
								description={t('Shop_ManageShop_Shipping_ShippingOrigins_Desc')}
							>
								<div className="row">
									<div className="col-md-6">
										<Field
											name="shippingFromCountryKey"
											component={PortalSelectMenu}
											items={countries}
											isRequired={true}
											label={t('Shop_ManageShop_Shipping_Label_Country')}
										/>
									</div>
								</div>
							</PortalPageSection>
							<ButtonsSection>
								<div className="row">
									<div className="col-sm-6" />
									<div className="col-sm-6">
										<div className="floatRight">
											<Button type="submit" disabled={pristine || submitting}>
												{t('BSPortal_Common_SaveChanges')}
											</Button>
											<div className="clear" />
										</div>
										<div className="clear" />
									</div>
								</div>
							</ButtonsSection>
						</form>
					</LoadingOverlayWrapper>
				</div>
			</ManageShopWrapper>
		);
	}
}

function mapStateToProps(state: IStore) {
	let currentFormValues: IShopProfile = null;
	if (state.form[c.FORM_NAME_MANAGE_SHOP] && state.form[c.FORM_NAME_MANAGE_SHOP].values) {
		currentFormValues = state.form[c.FORM_NAME_MANAGE_SHOP].values;
	} else {
		currentFormValues = state.manageShop.shop;
	}

	return {
		initialValues: state.manageShop.shop,
		manageShop: state.manageShop,
		currentFormValues,
		formErrors: getFormSyncErrors(c.FORM_NAME_MANAGE_SHOP)(state),
		countryDomainValues: state.domainValues.countries,
	};
}

let ManageShopShipping = reduxForm(
	{
		form: c.FORM_NAME_MANAGE_SHOP,
		enableReinitialize: true,
	},
	mapStateToProps,
)(ManageShopShippingForm);

ManageShopShipping = connect(mapStateToProps, (dispatch) => ({
	getShop: (id: string, isShipping: boolean) => dispatch(getShop(id, isShipping)),
	putShop: (form: IShopProfile) => dispatch(putShop(form)),
}))(ManageShopShipping);

export { ManageShopShipping };
